<script setup>
import { computed, getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { DriverStore } from '../pinia/Driver'
import emitter from '../utils/emitter'
import { dismissNotify, notify, useNotify } from '../composables/useNotify'
import NavRailItem from './NavRailItem.vue'
import WeeklyDropDialog from './WeeklyDropDialog.vue'

const READY_POLL = 45000
const EMPTY_POLL = 15000
const NOTICE_ID = 'wheelspin-available'

const { proxy } = getCurrentInstance()
const Driver = DriverStore()
const { state } = useNotify()
const dialog = ref(false)
let timer = null
let shownId = null
let loading = false

function countFrom(user) {
  if (!user || typeof user !== 'object' || Array.isArray(user)) return 0
  const value = Number(user.wheelspins ?? user.Wheelspins ?? user.WHEELSPINS)
  return Number.isFinite(value) ? Math.max(0, Math.floor(value)) : 0
}

const wheelspins = computed(() => countFrom(Driver.user))
const available = computed(() => wheelspins.value > 0)
const label = computed(() => {
  const count = wheelspins.value
  return count > 1 ? `Wheelspin (${count})` : 'Wheelspin'
})

function setCount(value) {
  const next = Math.max(0, Math.floor(Number(value) || 0))
  if (!Driver.user || typeof Driver.user !== 'object' || Array.isArray(Driver.user)) return
  if (countFrom(Driver.user) === next) return
  Driver.setUser({ ...Driver.user, wheelspins: next })
}
function clearTimer() {
  if (timer) {
    window.clearInterval(timer)
    timer = null
  }
}
function schedule() {
  clearTimer()
  if (!Driver.logged) return
  timer = window.setInterval(refresh, available.value ? READY_POLL : EMPTY_POLL)
}
function hideNotice() {
  if (state.id != null && shownId != null && state.id === shownId) dismissNotify()
  shownId = null
}
function showNotice() {
  if (!available.value) {
    hideNotice()
    return
  }
  shownId = NOTICE_ID
  const count = wheelspins.value
  const copy = count === 1 ? '1 wheelspin' : `${count} wheelspins`
  notify({
    id: NOTICE_ID,
    title: 'Wheelspin',
    message: `You have ${copy} ready — open Wheelspin in the nav or <button type="button" class="nr-notify-action" data-notify-action="open-wheelspin">tap here</button> to spin.`,
    type: 'info',
    icon: 'mdi:slot-machine',
    html: true,
    persistent: true,
  })
}
async function refresh() {
  if (!Driver.logged) {
    hideNotice()
    return
  }
  if (loading) return
  loading = true
  try {
    const status = await proxy.$api({ serviceName: 'premiumDrop', methodName: 'wheelSpinStatus', parameters: [] })
    if (status && status.wheelspins != null && status.wheelspins !== '') {
      const value = Number(status.wheelspins)
      if (Number.isFinite(value)) setCount(value)
    }
    showNotice()
  } catch {
    showNotice()
  } finally {
    loading = false
  }
}
function open() {
  if (available.value) dialog.value = true
}
function claimed(payload) {
  if (payload?.wheelspins != null) setCount(payload.wheelspins)
  if (wheelspins.value <= 0) hideNotice()
  else showNotice()
  schedule()
}
function onVisible() {
  if (document.visibilityState !== 'hidden') refresh()
}

onMounted(() => {
  refresh()
  schedule()
  emitter.on('open-wheelspin', open)
  emitter.on('refresh-wheelspins', refresh)
  window.addEventListener('focus', onVisible)
  document.addEventListener('visibilitychange', onVisible)
})
onBeforeUnmount(() => {
  clearTimer()
  emitter.off('open-wheelspin', open)
  emitter.off('refresh-wheelspins', refresh)
  window.removeEventListener('focus', onVisible)
  document.removeEventListener('visibilitychange', onVisible)
})
watch(() => [Driver.logged, wheelspins.value], ([logged]) => {
  if (!logged) {
    hideNotice()
    clearTimer()
    return
  }
  showNotice()
  schedule()
}, { immediate: true })
watch(() => Driver.logged, (logged) => {
  if (logged) refresh()
})
</script>

<template>
  <NavRailItem v-if="available" variant="wheelspin" icon="slot-machine" :label="label" pulse @click="open" />
  <WeeklyDropDialog v-model="dialog" mode="normal" :wheelspins="wheelspins" @claimed="claimed" />
</template>
